import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  Button,
  Stack,
  Text,
  Paper,
  Group,
  Alert,
  Space,
  MantineProvider,
  createTheme,
  Grid,
  Image,
  Divider,
  Tabs,
  Table,
  Badge,
  ActionIcon,
  Modal,
  ScrollArea,
  TextInput,
  Switch,
  Select,
  ColorInput,
  NumberInput,
  Textarea,
  JsonInput,
  Accordion,
  Fieldset,
} from "@mantine/core";
import { IconSettings, IconCheck, IconArrowBack, IconEye, IconClock, IconShieldCheck, IconX, IconRefresh, IconCode, IconPalette } from "@tabler/icons-react";
import { GlobalProvider, useGlobal } from "../../context/global-context";
import VXLogo from "../../assets/vx-full-logo.svg";
import PreviewPanel from "./preview-panel";
import { BasicTab, JourneyTab, IntegrationTab, StylingTab, DataTab, MessagesTab } from "./configuration-tabs";

const theme = createTheme({
  colors: {
    "ocean-blue": [
      "#B3D1FC",
      "#9BC3FA",
      "#82B5F8",
      "#6AA8F6",
      "#529BF3",
      "#3A8EF1",
      "#227FF0",
      "#096FFD",
      "#0060EA",
      "#0056D7",
    ],
    midnight: [
      "#1c1c1e",
      "#2c2c2e",
      "#3c3c3e",
      "#4c4c4e",
      "#5c5c5e",
      "#6c6c6e",
      "#1c1c1e",
      "#8c8c8e",
      "#9c9c9e",
      "#acacb0",
    ],
    "bright-pink": [
      "#F0BBDD",
      "#ED9BCF",
      "#EC7CC3",
      "#ED5DB8",
      "#F13EAF",
      "#F71FA7",
      "#FF00A1",
      "#E00890",
      "#C50E82",
      "#AD1374",
    ],
  },
  primaryColor: "midnight",
  defaultRadius: 5,
  breakpoints: {
    xs: "36em",
    sm: "48em",
    md: "62em",
    lg: "75em",
    xl: "88em",
  },
});

function ConfigurationContent() {
  const { globalState, updateAdvancedSettings, setContextValue, updateAddons } = useGlobal();
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionModalOpened, setSessionModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiBodyModal, setApiBodyModal] = useState(false);
  const [apiRequestBody, setApiRequestBody] = useState({});

  // Set the tab title
  useEffect(() => {
    document.title = "VX Configuration Center";
    return () => {
      document.title = "Demo React Next"; // Reset to default when leaving
    };
  }, []);

  // Mock verification sessions data
  const mockSessions = [
    {
      id: "vx_12345",
      timestamp: "2024-01-15 14:30:25",
      status: "PASS",
      journey: "ID Document with Express Options",
      age: "21+",
      method: "popup",
      duration: "2m 15s",
      ip: "192.168.1.100",
      userAgent: "Chrome 120.0.0.0",
      details: {
        documentType: "Driver License",
        issuer: "California DMV",
        verificationScore: 95,
        flags: [],
        biometrics: "Passed",
        address: "123 Main St, San Francisco, CA"
      }
    },
    {
      id: "vx_12344",
      timestamp: "2024-01-15 13:15:10",
      status: "FAIL",
      journey: "Phone to ID Document Express",
      age: "18+",
      method: "redirect",
      duration: "4m 33s",
      ip: "10.0.0.25",
      userAgent: "Safari 17.0",
      details: {
        documentType: "Passport",
        issuer: "US State Department",
        verificationScore: 45,
        flags: ["Document quality too low", "Face match failed"],
        biometrics: "Failed",
        address: "N/A"
      }
    },
    {
      id: "vx_12343",
      timestamp: "2024-01-15 11:45:18",
      status: "PASS",
      journey: "Phone Only",
      age: "25+",
      method: "popup",
      duration: "1m 45s",
      ip: "172.16.0.50",
      userAgent: "Firefox 121.0",
      details: {
        documentType: "N/A",
        issuer: "N/A",
        verificationScore: 88,
        flags: [],
        biometrics: "N/A",
        address: "456 Oak Ave, Los Angeles, CA"
      }
    }
  ];

  useEffect(() => {
    if (activeTab === "sessions") {
      // Simulate loading sessions
      setLoading(true);
      setTimeout(() => {
        setSessions(mockSessions);
        setLoading(false);
      }, 1000);
    }
  }, [activeTab]);

  const refreshSessions = () => {
    setLoading(true);
    setTimeout(() => {
      setSessions([...mockSessions]);
      setLoading(false);
    }, 800);
  };

  const viewSessionDetails = (session) => {
    setSelectedSession(session);
    setSessionModalOpened(true);
  };

  const handleSave = () => {
    localStorage.setItem("demoSettings", JSON.stringify(globalState));
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const generateDemoData = (field) => {
    const demoData = {
      fn: "John",
      ln: "Doe",
      addr: "123 Main Street",
      city: "San Francisco",
      state: "CA",
      zip: "94105",
      phone: "415-555-1234",
      dob: "19900101",
      ssn: "6789",
      email: "john.doe@example.com",
      reference: "DEMO-" + Date.now(),
    };
    return demoData[field];
  };

  const buildApiRequestBody = () => {
    const body = {
      // Basic settings from globalState
      age: globalState.age,
      journey: globalState.journey,
      choices: globalState.modularChoices,
      language: globalState.language,
      
      // Settings object
      settings: {
        ...globalState.advancedSettings?.settings,
        method: globalState.method,
        isCollectCustomerData: globalState.isCollectCustomerData,
      },
      
      // Webhook
      webhook: globalState.advancedSettings?.webhook,
      
      // Additional data
      additionalData: globalState.advancedSettings?.additionalData,
      
      // Styles
      styles: globalState.advancedSettings?.styles,
      
      // Result messages
      resultMessages: globalState.advancedSettings?.resultMessages,
      
      // Add-ons
      addons: Object.entries(globalState.addons).reduce((acc, [key, addon]) => {
        if (addon.checked) {
          acc[key] = true;
        }
        return acc;
      }, {}),
    };
    
    // Add demo customer data for selected fields
    const dataFields = globalState.advancedSettings?.dataFields || {};
    Object.keys(dataFields).forEach(field => {
      if (dataFields[field] && field !== 'custom') {
        body[field] = generateDemoData(field);
      }
    });
    
    // Add custom fields if selected
    if (dataFields.custom) {
      body.additionalData = {
        ...body.additionalData,
        customField1: "Custom Value 1",
        customField2: "Custom Value 2",
        timestamp: new Date().toISOString()
      };
    }
    
    return body;
  };

  const handleSaveAdvanced = () => {
    const requestBody = buildApiRequestBody();
    setApiRequestBody(requestBody);
    setApiBodyModal(true);
    
    // Still save to localStorage
    localStorage.setItem("demoSettings", JSON.stringify(globalState));
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handleGoBack = () => {
    window.close();
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
    }}>
      {/* Header with VX logo */}
      <Paper p="md" withBorder style={{ backgroundColor: '#fff', marginBottom: '2rem' }}>
        <Container size="xl">
          <Group justify="space-between" align="center">
            <Group>
              <Image src={VXLogo} w={120} />
              <div style={{ marginLeft: '1rem' }}>
                <Title order={2} c="dark.8">
                  Configuration Center
                </Title>
                <Text c="dimmed" size="sm">
                  Demo Settings & Preferences
                </Text>
              </div>
            </Group>
            <Button
              variant="light"
              leftSection={<IconArrowBack size={16} />}
              onClick={handleGoBack}
              size="md"
              color="gray"
            >
              Close Tab
            </Button>
          </Group>
        </Container>
      </Paper>

      <Container size="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List mb="lg">
            <Tabs.Tab value="configuration" leftSection={<IconSettings size={16} />}>
              Configuration & Settings
            </Tabs.Tab>
            <Tabs.Tab value="sessions" leftSection={<IconClock size={16} />}>
              Verification Sessions
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="configuration">
            <Grid gutter="lg">
              {/* Left Panel - Configuration Settings */}
              <Grid.Col span={7}>
                <Paper p="lg" withBorder radius="md" style={{ backgroundColor: '#fff', height: 'calc(100vh - 250px)' }}>
                  <Tabs defaultValue="basic">
                    <Tabs.List mb="lg">
                      <Tabs.Tab value="basic" leftSection={<IconSettings size={14} />}>
                        Basic
                      </Tabs.Tab>
                      <Tabs.Tab value="journey" leftSection={<IconShieldCheck size={14} />}>
                        Journey
                      </Tabs.Tab>
                      <Tabs.Tab value="integration" leftSection={<IconCode size={14} />}>
                        Integration
                      </Tabs.Tab>
                      <Tabs.Tab value="styling" leftSection={<IconPalette size={14} />}>
                        Styling
                      </Tabs.Tab>
                      <Tabs.Tab value="data" leftSection={<IconEye size={14} />}>
                        Data
                      </Tabs.Tab>
                      <Tabs.Tab value="messages" leftSection={<IconCheck size={14} />}>
                        Messages
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="basic">
                      <BasicTab globalState={globalState} />
                    </Tabs.Panel>

                    <Tabs.Panel value="journey">
                      <JourneyTab 
                        globalState={globalState}
                        setContextValue={setContextValue}
                        updateAdvancedSettings={updateAdvancedSettings}
                      />
                    </Tabs.Panel>

                    <Tabs.Panel value="integration">
                      <IntegrationTab 
                        globalState={globalState}
                        setContextValue={setContextValue}
                        updateAddons={updateAddons}
                      />
                    </Tabs.Panel>

                    <Tabs.Panel value="styling">
                      <StylingTab 
                        globalState={globalState}
                        updateAdvancedSettings={updateAdvancedSettings}
                      />
                    </Tabs.Panel>

                    <Tabs.Panel value="data">
                      <DataTab 
                        globalState={globalState}
                        updateAdvancedSettings={updateAdvancedSettings}
                      />
                    </Tabs.Panel>

                    <Tabs.Panel value="messages">
                      <MessagesTab 
                        globalState={globalState}
                        updateAdvancedSettings={updateAdvancedSettings}
                      />
                    </Tabs.Panel>
                  </Tabs>

                  {/* Save Button */}
                  <Group justify="center" mt="lg" pt="lg" style={{ borderTop: '1px solid #e9ecef' }}>
                    <Button
                      size="md"
                      leftSection={<IconCheck size={16} />}
                      onClick={handleSaveAdvanced}
                      gradient={{ from: 'blue', to: 'indigo' }}
                      variant="gradient"
                    >
                      Save All Settings
                    </Button>
                  </Group>
                </Paper>
              </Grid.Col>

              {/* Right Panel - Preview */}
              <Grid.Col span={5}>
                <PreviewPanel globalState={globalState} />
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="sessions">
            <Paper p="xl" withBorder radius="md" style={{ backgroundColor: '#fff' }}>
              <Group justify="space-between" mb="lg">
                <div>
                  <Title order={3} c="dark.8">Verification Sessions</Title>
                  <Text c="dimmed" size="sm">View recent verification attempts and their results</Text>
                </div>
                <Button 
                  leftSection={<IconRefresh size={16} />}
                  onClick={refreshSessions}
                  loading={loading}
                  variant="light"
                >
                  Refresh
                </Button>
              </Group>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text c="dimmed">Loading sessions...</Text>
                </div>
              ) : (
                <ScrollArea>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Session ID</Table.Th>
                        <Table.Th>Timestamp</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Journey</Table.Th>
                        <Table.Th>Duration</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sessions.map((session) => (
                        <Table.Tr key={session.id}>
                          <Table.Td>
                            <Text size="sm" fw={500}>{session.id}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{session.timestamp}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge 
                              color={session.status === 'PASS' ? 'green' : 'red'}
                              variant="light"
                            >
                              {session.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{session.journey}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{session.duration}</Text>
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              variant="light"
                              size="sm"
                              onClick={() => viewSessionDetails(session)}
                            >
                              <IconEye size={14} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>
          </Tabs.Panel>

        </Tabs>
      </Container>

      {/* Session Details Modal */}
      <Modal
        opened={sessionModalOpened}
        onClose={() => setSessionModalOpened(false)}
        title={`Session Details - ${selectedSession?.id}`}
        size="lg"
        centered
      >
        {selectedSession && (
          <Stack gap="lg">
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed">Status</Text>
                <Badge 
                  color={selectedSession.status === 'PASS' ? 'green' : 'red'}
                  size="lg"
                  variant="light"
                >
                  {selectedSession.status}
                </Badge>
              </div>
              <div>
                <Text size="sm" c="dimmed">Score</Text>
                <Text fw={500}>{selectedSession.details.verificationScore}/100</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Duration</Text>
                <Text fw={500}>{selectedSession.duration}</Text>
              </div>
            </Group>

            <Divider />

            <Grid>
              <Grid.Col span={6}>
                <Stack gap="sm">
                  <div>
                    <Text size="sm" c="dimmed">Journey Type</Text>
                    <Text fw={500}>{selectedSession.journey}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Age Requirement</Text>
                    <Text fw={500}>{selectedSession.age}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Method</Text>
                    <Text fw={500} tt="capitalize">{selectedSession.method}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Document Type</Text>
                    <Text fw={500}>{selectedSession.details.documentType}</Text>
                  </div>
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Stack gap="sm">
                  <div>
                    <Text size="sm" c="dimmed">Issuer</Text>
                    <Text fw={500}>{selectedSession.details.issuer}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Biometrics</Text>
                    <Text fw={500}>{selectedSession.details.biometrics}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">IP Address</Text>
                    <Text fw={500}>{selectedSession.ip}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">User Agent</Text>
                    <Text fw={500}>{selectedSession.userAgent}</Text>
                  </div>
                </Stack>
              </Grid.Col>
            </Grid>

            {selectedSession.details.flags.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text size="sm" c="dimmed" mb="xs">Issues</Text>
                  <Stack gap="xs">
                    {selectedSession.details.flags.map((flag, index) => (
                      <Alert key={index} color="red" variant="light">
                        {flag}
                      </Alert>
                    ))}
                  </Stack>
                </div>
              </>
            )}
          </Stack>
        )}
      </Modal>

      {/* API Request Body Modal */}
      <Modal
        opened={apiBodyModal}
        onClose={() => setApiBodyModal(false)}
        title="API Request Body for generate-link"
        size="xl"
        centered
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            This is the complete request body that will be sent to the generate-link endpoint:
          </Text>
          <ScrollArea h={500}>
            <JsonInput
              value={JSON.stringify(apiRequestBody, null, 2)}
              readOnly
              minRows={20}
              maxRows={30}
            />
          </ScrollArea>
          <Group justify="space-between">
            <Button 
              variant="light" 
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(apiRequestBody, null, 2));
                alert('Copied to clipboard!');
              }}
            >
              Copy to Clipboard
            </Button>
            <Button onClick={() => setApiBodyModal(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}

export default function ConfigurationPage() {
  return (
    <ConfigurationContent />
  );
}
